import { useLocation, useNavigate } from 'react-router-dom';
import { useSavedEvents } from '../../hooks';
import { useDiscoveryNav } from '../../hooks/useDiscoveryNav';
import { useUserAuth } from '../../contexts/UserAuthContext';
import styles from './BottomTabBar.module.css';

interface Tab {
  path: string;
  label: string;
  icon: string;
}

const tabs: Tab[] = [
  { path: '/', label: 'Home', icon: 'home' },
  { path: '/esplora', label: 'Esplora', icon: 'compass' },
  { path: '/salvati', label: 'Salvati', icon: 'heart' },
  { path: '/profilo', label: 'Profilo', icon: 'person' },
];

function TabIcon({ icon, active }: { icon: string; active: boolean }) {
  const stroke = active ? 'var(--color-primary)' : 'var(--color-text-muted)';

  switch (icon) {
    case 'home':
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke={stroke}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path
            d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"
            fill={active ? stroke : 'none'}
            opacity={active ? 0.2 : 1}
          />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      );
    case 'compass':
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke={stroke}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <polygon
            points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"
            fill={active ? stroke : 'none'}
          />
        </svg>
      );
    case 'heart':
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill={active ? stroke : 'none'}
          stroke={stroke}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
        </svg>
      );
    case 'person':
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke={stroke}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="8" r="5" fill={active ? stroke : 'none'} opacity={active ? 0.2 : 1} />
          <path d="M20 21a8 8 0 0 0-16 0" />
        </svg>
      );
    case 'calendar':
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke={stroke}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect
            x="3"
            y="4"
            width="18"
            height="18"
            rx="2"
            ry="2"
            fill={active ? stroke : 'none'}
            opacity={active ? 0.2 : 1}
          />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      );
    default:
      return null;
  }
}

export function BottomTabBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { savedCount } = useSavedEvents();
  const { user } = useUserAuth();
  // T013: in v2 the Home/Esplora tabs navigate to the territorial surfaces of
  // the current URL through the shared builder; legacy keeps the literals.
  const nav = useDiscoveryNav();

  // Hide on event detail pages and login pages
  if (
    location.pathname.startsWith('/event/') ||
    location.pathname === '/login' ||
    location.pathname === '/accedi' ||
    location.pathname === '/registrati' ||
    location.pathname === '/password-reset' ||
    location.pathname === '/password-update' ||
    location.pathname === '/auth/callback'
  ) {
    return null;
  }

  const handleTabClick = (tab: Tab) => {
    if (tab.path === '/profilo' && !user) {
      // Pass backgroundLocation so AuthModal can overlay on desktop;
      // ignored on mobile (BottomTabBar is mobile-first but useIsDesktop
      // gate in App.tsx makes this safe in either case).
      navigate('/accedi', { state: { backgroundLocation: location } });
      return;
    }
    if (nav.v2) {
      // v2: Home and Esplora land on the territorial surfaces of the current
      // URL (same targets as the TopBar anchors); other tabs keep literals.
      if (tab.path === '/') {
        navigate(nav.homeUrl);
        return;
      }
      if (tab.path === '/esplora') {
        navigate(nav.esploraUrl);
        return;
      }
    }
    navigate(tab.path);
  };

  // v2 active-tab rules: Home/Esplora follow the discovery surface (a
  // category path is an Esplora surface); everything else is exact-match.
  const isTabActive = (tab: Tab): boolean => {
    if (tab.path === '/profilo') {
      return location.pathname === '/profilo' || location.pathname === '/accedi';
    }
    if (tab.path === '/' && nav.v2) return nav.surface === 'home';
    if (tab.path === '/esplora' && nav.v2) return nav.surface === 'esplora';
    return location.pathname === tab.path;
  };

  return (
    <nav className={styles.nav} aria-label="Navigazione principale">
      {tabs.map((tab) => {
        const isActive = isTabActive(tab);
        return (
          <button
            key={tab.path}
            className={`${styles.tab} ${isActive ? styles.active : ''}`}
            onClick={() => handleTabClick(tab)}
            aria-current={isActive ? 'page' : undefined}
            aria-label={tab.label}
          >
            <span className={styles.iconWrapper}>
              <TabIcon icon={tab.icon} active={isActive} />
              {tab.icon === 'heart' && savedCount > 0 && (
                <span className={styles.badge}>{savedCount > 99 ? '99+' : savedCount}</span>
              )}
            </span>
            <span className={styles.label}>{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
